<template>
  <div>
    <el-table :data="rows" stripe :row-class-name="tableRowClassName" :cell-style="tableCellStyle" :class="$style.table" v-loading="loading" @sort-change="$emit('sort-change', $event)">
      <el-table-column type="expand">
        <template slot-scope="scope">
          <div :class="$style.expandArea">
            <div
              ><strong>{{ labels.periodLabel }}</strong
              >{{ scope.row.startDate || labels.empty }} ~ {{ scope.row.endDate || labels.empty }}</div
            >
            <div
              ><strong>{{ labels.roomTypeLabel }}</strong
              >{{ (scope.row.roomTypeIds || []).map(getRoomName).join('、') || labels.empty }}</div
            >
          </div>
        </template>
      </el-table-column>

      <el-table-column :label="labels.itemName" min-width="180" class-name="item-name-col" label-class-name="item-name-header">
        <template slot-scope="scope">
          <span :class="$style.titleText">{{ scope.row.title || labels.empty }}</span>
        </template>
      </el-table-column>

      <el-table-column prop="type" sortable="custom" :label="labels.type" width="110" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.type === 'stay' ? 'primary' : 'warning'" size="small" effect="plain" :class="scope.row.type === 'stay' ? $style.typeTagStay : $style.typeTagPeriod">
            {{ typeText(scope.row.type) }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="adjustValue" sortable="custom" :label="labels.adjustValue" width="150" align="center" label-class-name="price-adjust-header">
        <template slot-scope="scope">
          <div :class="$style.changeCell">
            <span v-if="isNoChange(scope.row)" :class="[$style.priceNeutral, $style.changeAmount]">{{ labels.noChange }}</span>
            <span v-else :class="[scope.row.operator === '+' ? $style.priceUp : $style.priceDown, $style.changeAmount]">
              {{ scope.row.operator === '+' ? '＋' : '－' }}{{ valueText(scope.row) }}
            </span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="minNight" sortable="custom" :label="labels.minNight" width="120" align="center" class-name="is-min-night-col" label-class-name="is-min-night-col-header">
        <template slot-scope="scope">
          <span v-if="scope.row.type === 'period'" :class="$style.emptyStatus">{{ labels.emptySymbol }}</span>
          <span v-else :class="$style.minNightText">{{ scope.row.minNight }} {{ labels.nightSuffix }}</span>
        </template>
      </el-table-column>

      <el-table-column prop="periodStatus" sortable="custom" :label="labels.periodStatus" width="150" align="center" label-class-name="period-status-header">
        <template slot-scope="scope">
          <el-tag v-if="isExpired(scope.row)" type="danger" size="small" effect="dark">{{ labels.expired }}</el-tag>
          <div v-else-if="scope.row.startDate || scope.row.endDate" :class="$style.periodDateCell">
            <div :class="$style.periodDateText">{{ formatDate(scope.row.startDate) }}</div>
            <div :class="$style.periodDateText">{{ formatDate(scope.row.endDate) }}</div>
          </div>
          <span v-else :class="$style.emptyStatus">{{ labels.emptySymbol }}</span>
        </template>
      </el-table-column>

      <el-table-column :label="labels.actions" width="140" align="center">
        <template slot-scope="scope">
          <el-button type="text" :class="$style.actionButton" @click="$emit('edit', scope.row)">{{ labels.edit }}</el-button>
          <span :class="$style.actionDivider">|</span>
          <el-button type="text" :class="[$style.actionButton, $style.deleteButton]" @click="$emit('delete', scope.row)">{{ labels.delete }}</el-button>
        </template>
      </el-table-column>

      <el-table-column :label="labels.enabled" width="100" align="center">
        <template slot-scope="scope">
          <el-switch v-model="scope.row.state" :active-value="1" :inactive-value="0" @change="$emit('toggle-state', scope.row)"></el-switch>
        </template>
      </el-table-column>
    </el-table>

    <div :class="$style.paginationWrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next"
        :total="total"
        :current-page="currentPage"
        :page-size="pageSize"
        :page-sizes="pageSizes"
        @size-change="$emit('page-size-change', $event)"
        @current-change="$emit('page-change', $event)"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PriceAdjustmentTable',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    pageSizes: {
      type: Array,
      default: () => [10, 20, 50],
    },
    roomNameResolver: {
      type: Function,
      default: null,
    },
    typeTextMap: {
      type: Object,
      default: () => ({ stay: '連續住宿', period: '期間限定' }),
    },
    labels: {
      type: Object,
      default: () => ({
        roomTypeLabel: '適用房型：',
        periodLabel: '期間：',
        itemName: '項目名稱',
        type: '適用類型',
        adjustValue: '價格調整',
        minNight: '連住天數',
        periodStatus: '期間狀態',
        actions: '操作',
        enabled: '啟用',
        edit: '編輯',
        delete: '刪除',
        expired: '已過期',
        noChange: '無變動',
        empty: '-',
        emptySymbol: '－',
        nightSuffix: '晚以上',
      }),
    },
  },
  methods: {
    getRoomName(roomId) {
      if (typeof this.roomNameResolver === 'function') {
        return this.roomNameResolver(roomId)
      }
      return String(roomId)
    },
    typeText(type) {
      return this.typeTextMap[type] || this.typeTextMap.stay
    },
    valueText(row) {
      const unit = row.valueType === '%' ? '%' : '元'
      return `${row.value}${unit}`
    },
    isNoChange(row) {
      return Number(row.value) === 0
    },
    isExpired(row) {
      return row?.isExpired === true
    },
    formatDate(value) {
      if (!value) return this.labels.emptySymbol
      if (typeof value === 'string') {
        const normalized = value.slice(0, 10).replace(/-/g, '/')
        return normalized || this.labels.emptySymbol
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return this.labels.emptySymbol
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}/${month}/${day}`
    },
    tableRowClassName({ row }) {
      return this.isExpired(row) ? 'expired-row' : ''
    },
    tableCellStyle({ row }) {
      if (!this.isExpired(row)) return null
      return {
        backgroundColor: '#ffe8e5',
      }
    },
  },
}
</script>

<style lang="scss" module scoped>
.table {
  border-radius: 6px;
  overflow: hidden;

  ::v-deep .el-table__header th {
    background: #f9fafb;
    color: #2c3e50;
    font-weight: 600;
    border-bottom: 2px solid #e4e7eb;
  }

  ::v-deep .el-table th,
  ::v-deep .el-table td {
    border-right: none;
    border-color: #e4e7eb;
  }

  ::v-deep .el-table__body td .cell {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ::v-deep .item-name-col .cell,
  ::v-deep .item-name-header .cell {
    justify-content: flex-start;
    text-align: left;
  }

  ::v-deep .period-status-header .cell,
  ::v-deep .price-adjust-header .cell {
    justify-content: center;
    text-align: center;
  }

  ::v-deep .is-min-night-col-header .cell {
    padding-left: 12px;
  }

  ::v-deep .el-table__expand-icon {
    transition: transform 0.3s ease;
  }

  ::v-deep .el-table__expand-icon--expanded {
    transform: rotate(90deg);
  }

  ::v-deep .expired-row > td:not(:nth-child(2)) {
    color: #a0a6ad;
  }
}

.priceUp {
  color: #2fb36e;
  font-weight: 700;
  font-size: 14px;
}

.priceDown {
  color: #f56c6c;
  font-weight: 600;
  font-size: 14px;
}

.priceNeutral {
  color: #a0a6ad;
  font-weight: 500;
  font-size: 13px;
}

.changeCell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
}

.changeAmount {
  min-width: 120px;
  align-self: center;
  text-align: center;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.2px;
}

.titleText {
  color: #1f2d3d;
  font-weight: 700;
}

.typeTagStay {
  color: #d46b08;
  border-color: #f7c58d;
  background: #fff5e9;
}

.typeTagPeriod {
  color: #1f8f6b;
  border-color: #8be0c7;
  background: #e8fbf5;
}

.minNightText {
  color: #606266;
}

.expandArea {
  line-height: 1.9;
  color: #606266;
  background: #f9fafb;
  border: 1px solid #e4e7eb;
  border-radius: 4px;
  padding: 12px;
}

.emptyStatus {
  color: #c0c4cc;
  font-size: 15px;
  line-height: 1;
}

.periodDateCell {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.25;
}

.periodDateText {
  color: #606266;
  font-size: 14px;
}

.paginationWrap {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.actionButton {
  color: #606266;
}

.actionButton:hover,
.actionButton:focus {
  color: #303133;
}

.actionDivider {
  color: #dcdfe6;
  margin: 0 2px;
}

.deleteButton {
  color: #a0a6ad;
}

.deleteButton:hover,
.deleteButton:focus {
  color: #f56c6c;
}
</style>
